import { createContext } from "preact"
import { useContext, useEffect, useRef, useState } from "preact/hooks"
import { qs } from "../util/dom"
import metrics from "../util/metrics"

const loadingEl = qs("#loading")
const headerEl = qs("header")

const FormContext = createContext()

export const useFormContext = () => {
  return useContext(FormContext)
}

export const FormProvider = ({ children, ep, contactEp }) => {
  const [step, setStep] = useState(0)
  const [stepClass, _setStepClass] = useState("from-right")
  const [storedValues, setStoredValues] = useState([])
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({})
  const [modalData, setModalData] = useState({})
  const [legalModalShown, setLegalModalShown] = useState([])
  const [summaryActive, setSummaryActive] = useState(false)
  const [messageData, setMessageData] = useState({})
  const [help, setHelp] = useState(undefined)
  const formRef = useRef()

  const popstateEventHandler = (e) => {
    if (e.state !== null) {
      showStep(0, e.state.step, false)
    } else window.location = ""
  }

  const handleCityChange = (e) => {
    ep.getData(`steps&city=${e.detail}`).then((epData) => {
      document.body.dataset.city = `${e.detail}`
      setData(epData)
    })
  }

  useEffect(() => {
    const city = document.body.dataset.city
    if (city !== "custom") {
      toggleValue({ name: "city", value: city })
    }
    window.addEventListener("meldetool_city-change", handleCityChange)
    return () => {
      window.removeEventListener("meldetool_city-change", handleCityChange)
    }
  }, [])

  /**
   * Load endpoint data
   */
  if (!data.steps) {
    ep.getData(`steps&city=${document.body.dataset.city}`).then((epData) => {
      setData(epData)
      setLoading(false)
    })
  }

  /**
   * - scroll to top on step progression
   */
  useEffect(() => {
    if (data.steps) metrics(data.steps[step].slug)
    const marginTop = headerEl.offsetHeight
    formRef.current.style = `margin-top: ${marginTop}px`
    window.scrollTo(0, 0)
  }, [step, data.steps])

  useEffect(() => {
    loadingEl.classList.toggle("collapse", !loading)
    setTimeout(document.body.classList.toggle("loading", loading), 300)
  }, [loading])

  useEffect(() => {
    if (data.steps) window.addEventListener("popstate", popstateEventHandler)
  }, [data.steps])

  useEffect(() => {
    if (data.steps) metrics(data.steps[step]?.slug)
    window.scrollTo(0, 0)
  }, [step, data.steps])

  /**
   * Switch to a step id or a relative position (c) in step array
   */
  const showStep = (c = 0, id = 0, push = true) => {
    id = Number.isInteger(id) ? id : data.steps?.findIndex((s) => s.slug === id)
    const nextStep = Number(c) !== 0 ? step + Number(c) : Number(id)

    // NOTNICE: this checks for summary in the slug, which is not nice, as you have to know it.
    if (
      !summaryActive &&
      /(?:^|-)summary(?:$|-)/.test(data.steps[nextStep]?.slug)
    )
      setSummaryActive(true)

    if (data.steps[nextStep]?.slug === "submission") {
      window.removeEventListener("popstate", popstateEventHandler)
    }

    if (push && nextStep > 0) {
      history.pushState({ step: nextStep }, "Meldetool Reporting")
    }
    setStep(nextStep)
    window.scrollTo(0, 0)
  }

  /**
   * Find identical and similar input values and toggle or just remove according to single and multiple choices
   */
  const toggleValue = (
    inputObj,
    deleteSimilar = false,
    removeSpecific = false,
  ) => {
    toggleModal(inputObj)

    let newValues = [...storedValues]

    const i_identical = newValues.findIndex(
      (o) => JSON.stringify(o) === JSON.stringify(inputObj),
    )

    if (i_identical >= 0) {
      newValues.splice(i_identical, 1)
    } else {
      if (deleteSimilar === true) {
        newValues = newValues.filter((o) => o.name !== inputObj.name)
      } else if (typeof deleteSimilar === "number") {
        const currentCount = newValues.filter(
          (o) => o.name === inputObj.name,
        ).length
        if (currentCount >= deleteSimilar) {
          return
        }
      }
      if (removeSpecific) {
        newValues = newValues.filter((o) => !removeSpecific.includes(o.name))
      }
      newValues.push(inputObj)
    }

    setStoredValues(newValues)
  }

  /**
   * Directly remove values with certain name from the store
   */
  const removeValue = (name) => {
    let newValues = [...storedValues]
    newValues = newValues.filter((o) => o.name !== name)
    setStoredValues(newValues)
  }

  /**
   * Check if a value exists in form values
   */
  const inFormValues = (valObj) => {
    const match = storedValues.find((o) => {
      return (
        (valObj.id ? o.step === valObj.id : true) &&
        (valObj.name ? o.name === valObj.name : true) &&
        (valObj.value ? o.value === valObj.value : true)
      )
    })
    return match
  }

  /**
   * Show legal modal if toggled by any values in store
   */
  const toggleModal = ({ name, value }) => {
    if (data.modals) {
      const newModal = data.modals.find((modal) =>
        modal.toggles.some(
          (toggle) => toggle.name === name && toggle.value === value,
        ),
      )
      if (newModal && !legalModalShown.includes(`${name}-${value}`)) {
        setModalData(newModal)
        const newValues = [...legalModalShown]
        newValues.push(`${name}-${value}`)
        setLegalModalShown(newValues)
      }
    }
  }

  const resolveCityRequirements = (requirement) => {
    let resolve = true
    if (requirement === "city") {
      resolve =
        (inFormValues({ name: "city" }) &&
          inFormValues({ name: "city" }).value !== "custom") ||
        inFormValues({ name: "custom_city" })
    }
    return resolve
  }

  /**
   * Determines whether any or any specific required input is missing
   */
  const requirementsMet = (name = undefined) => {
    const requirements = data.steps[step]?.requirements

    let requirementsAreMet = true
    if (name && requirements) {
      requirementsAreMet =
        requirements.includes(name) &&
        storedValues.some((value) => value.name === name)
    } else if (requirements) {
      requirementsAreMet = requirements.every((requirement) => {
        return (
          storedValues.some((value) => value.name === requirement) &&
          resolveCityRequirements(requirement)
        )
      })
    }

    return requirementsAreMet
  }

  /**
   * Outline required fields
   */
  const showHelp = () => {
    if (!requirementsMet()) {
      setHelp(true)
      setTimeout(() => {
        setHelp(false)
      }, 5000)
    }
  }

  /**
   * Handle form submission
   */
  const onSubmit = async (
    valueStore = storedValues,
    contactForm = false,
    redirect = false,
  ) => {
    const endpoint = contactForm ? contactEp : ep

    setLoading(true)

    const formData = new FormData()

    valueStore.map((o) => {
      formData.append(o.name, o.value)
    })

    const response = await endpoint.postData(formData)

    if (response.valid) {
      if (contactForm) {
        redirect && showStep(0, Number(redirect))
        setMessageData({
          message: gettext("Consultation request sent!"),
          severity: "ok",
        })
      } else {
        showStep(1)
      }
    } else handleValidationErrors(response)

    setModalData({})
    setLoading(false)
  }

  const handleValidationErrors = (response) => {
    console.log("errorResponseObject", response)
    const message = response.message || gettext("Something went wrong")
    setMessageData({
      message,
      severity: "error",
    })
  }

  const value = {
    step,
    stepClass,
    storedValues,
    loading,
    data,
    modalData,
    summaryActive,
    messageData,
    help,
    formRef,
    ep,
    contactEp,
    setModalData,
    showStep,
    toggleValue,
    removeValue,
    inFormValues,
    requirementsMet,
    showHelp,
    onSubmit,
    setStoredValues,
  }

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}
